import React, { useContext, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { DataContext } from '../../../contexto/ContextoDatos';
import { useScrollAnimation } from '../../../ganchos/usarAnimacionScroll';
import ProductCard from './TarjetaProducto';
import styles from './TopCategorias.module.css';

const formatCategoryName = (name) => {
  const words = name.split(' ');
  if (words.length <= 1) return name;
  const firstWord = words[0];
  const rest = words.slice(1).join(' ');
  return (
    <>
      <span className={styles.textBlue}>{firstWord}</span> {rest}
    </>
  );
};

const CategoryCard = ({ category, count, firstProduct, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const imgSrc = firstProduct ? `/imagenes/productos/${firstProduct.slug}-1.webp` : null;

  return (
    <button className={styles.categoryCard} onClick={onClick}>
      <div className={styles.imageWrapper}>
        {imgSrc && !imgError ? (
          <img 
            src={imgSrc} 
            alt={category.name} 
            className={styles.catImage}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={styles.placeholder}>
             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="32" height="32">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
             </svg>
          </div>
        )}
      </div>
      <div className={styles.textContent}>
        <h3 className={styles.catTitle}>{formatCategoryName(category.name)}</h3>
        <span className={styles.catCount}>({count} productos)</span>
      </div>
    </button>
  );
};

export const TopCategorias = () => {
  const { products, categories } = useContext(DataContext);
  const [selectedCategorySlug, setSelectedCategorySlug] = useState(null);
  const [ref, visible] = useScrollAnimation({ threshold: 0.1 });
  
  const sliderRef = useRef(null);
  const productsSliderRef = useRef(null);

  const scrollSlider = (targetRef, direction) => {
    if (targetRef.current) {
      const container = targetRef.current;
      const scrollAmount = container.clientWidth;
      
      if (direction === 1) { // next
        if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 10) {
          container.scrollTo({ left: 0, behavior: 'smooth' }); // wrap to start
        } else {
          container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      } else { // prev
        if (container.scrollLeft <= 10) {
          container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' }); // wrap to end
        } else {
          container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        }
      }
    }
  };

  // Calculate counts and find representative product
  const enrichedCategories = useMemo(() => {
    return categories.map(cat => {
      const catProducts = products.filter(p => p.category_slug === cat.slug);
      return {
        ...cat,
        count: catProducts.length,
        firstProduct: catProducts[0] // Just take the first one as representative
      };
    })
    .filter(cat => cat.count > 0) // Hide empty categories
    .sort((a, b) => b.count - a.count); // Sort by highest count
  }, [categories, products]);

  // Products to show if a category is selected
  const displayProducts = useMemo(() => {
    if (!selectedCategorySlug) return [];
    return products.filter(p => p.category_slug === selectedCategorySlug);
  }, [selectedCategorySlug, products]);

  const selectedCategoryObj = enrichedCategories.find(c => c.slug === selectedCategorySlug);

  return (
    <div className={`${styles.wrapper} ${visible ? styles.visible : ''}`} ref={ref}>
      <div className={styles.header}>
        {!selectedCategorySlug ? (
          <div className={styles.mainTitleWrapper}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.textBlue}>CATEGORÍAS</span> PRINCIPALES
            </h2>
            <div className={styles.lineaTitulo} />
          </div>
        ) : (
          <div className={styles.drilldownHeader}>
            <button className={styles.btnBack} onClick={() => setSelectedCategorySlug(null)}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" width="18" height="18">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              Volver a categorías
            </button>
            <h2 className={styles.drilldownTitle}>
              {selectedCategoryObj ? formatCategoryName(selectedCategoryObj.name) : null}
            </h2>
          </div>
        )}
      </div>

      {!selectedCategorySlug ? (
        <>
          <div className={styles.sliderContainer}>
            <button className={styles.navBtn} onClick={() => scrollSlider(sliderRef, -1)} aria-label="Anterior">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" width="20" height="20">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>
            
            <div className={styles.slider} ref={sliderRef}>
              {enrichedCategories.map(cat => (
                <CategoryCard 
                  key={cat.slug}
                  category={cat}
                  count={cat.count}
                  firstProduct={cat.firstProduct}
                  onClick={() => setSelectedCategorySlug(cat.slug)}
                />
              ))}
            </div>

            <button className={styles.navBtn} onClick={() => scrollSlider(sliderRef, 1)} aria-label="Siguiente">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" width="20" height="20">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </div>

          <div className={styles.catalogoBtnWrapper}>
            <Link to="/productos" className={styles.btnCatalogoCompleto}>
              Ver Catálogo Completo
            </Link>
          </div>
        </>
      ) : (
        <div className={styles.sliderContainer}>
          <button className={styles.navBtn} onClick={() => scrollSlider(productsSliderRef, -1)} aria-label="Anterior">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" width="20" height="20">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>

          <div className={styles.productsGrid} ref={productsSliderRef}>
            {displayProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <button className={styles.navBtn} onClick={() => scrollSlider(productsSliderRef, 1)} aria-label="Siguiente">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" width="20" height="20">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default TopCategorias;
